import * as React from 'react';
import { Modal, Button, Select, Input, message } from 'antd';
import { useTranslation } from 'react-i18next';
import { InfoCircleOutlined } from '@ant-design/icons';
import withStyles from 'react-jss';
import { useGenericState } from 'src/library/UseGenericState';
import { getFailureReasonList } from './DeliveryAcknowledgement.utils';
import { connect } from 'react-redux';

const styles: any = (theme) => ({
  modalFooter: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: 8,
  },
  infoBox: {
    display: 'flex',
    alignItems: 'flex-start',
    background: '#F0F7FF',
    border: '1px solid #BFDBFE',
    borderRadius: 8,
    padding: '10px 14px',
    marginBottom: 16,
  },
  infoIcon: {
    fontSize: 18,
    color: '#1677ff',
    marginRight: 10,
    marginTop: 2,
  },
  infoText: {
    fontSize: 13,
    color: '#1E3A8A',
    lineHeight: 1.5,
  },
  section: {
    marginBottom: 16,
  },
  sectionLabel: {
    marginBottom: 6,
    fontWeight: 500,
  },
  commentLabel: {
    marginBottom: 6,
    fontWeight: 500,
  },
  titleContainer: {
    fontSize: 15,
    fontWeight: 600,
  },
  titleSuffix: {
    color: '#0751b8ff',
    marginLeft: 6,
  },
});

interface NdrNprReasonModalProps {
  classes: any;
  isVisible: boolean;
  actionType: string;
  onCancel: () => void;
  allFailureReasons: Array<any>;
  onConfirm: (
    payload: { reasonId?: string; reasonName?: string; comment?: string }
  ) => Promise<void> | void;
  selectedRows?: Array<string>;
}

const NdrNprReasonModal = (props: NdrNprReasonModalProps) => {
  const {
    classes,
    isVisible,
    actionType,
    onCancel,
    onConfirm,
    selectedRows,
    allFailureReasons = [],
  } = props;

  const { t } = useTranslation();

  const [state, setState] = useGenericState({
    selectedReasonId: undefined as string | undefined,
    comment: '',
    isLoading: false,
  });

  const { selectedReasonId, comment, isLoading } = state;

  React.useEffect(() => {
    if (!isVisible) {
      setState({ selectedReasonId: undefined, comment: '', isLoading: false });
    }
  }, [isVisible]);

  const isDelivery = actionType === 'delivery';
  const reasonList = getFailureReasonList(allFailureReasons, actionType);
  const showReason = Array.isArray(reasonList) && reasonList.length > 0;

  const handleConfirm = async () => {
    if (showReason && !selectedReasonId) {
      message.error(t(isDelivery ? 'Please select the NDR Reason' : 'Please select the NPR Reason'));
      return;
    }
    const selectedReason = reasonList.find((r) => r.id === selectedReasonId);
    setState({ isLoading: true });

    try {
      await onConfirm({
        reasonId: selectedReason?.id,
        reasonName: selectedReason?.name,
        comment,
      });
    } finally {
      setState({ isLoading: false });
    }
  };

  const renderHeader = () => (
    <div className={classes.titleContainer}>
      {isDelivery ? t('Mark Undelivered') : t('Mark Not Picked Up')}
      {Array.isArray(selectedRows) && selectedRows.length === 1 ? (
        <span className={classes.titleSuffix}>- {selectedRows[0]}</span>
      ) : (
        <span className={classes.titleSuffix}> - Bulk Update</span>
      )}
    </div>
  );

  return (
    <Modal
      visible={isVisible}
      title={renderHeader()}
      onCancel={onCancel}
      width={600}
      centered
      footer={
        <div className={classes.modalFooter}>
          <Button onClick={onCancel} disabled={isLoading}>
            {t('Cancel')}
          </Button>
          <Button
            type="primary"
            onClick={handleConfirm}
            disabled={showReason && !selectedReasonId}
            loading={isLoading}
          >
            {t('Submit')}
          </Button>
        </div>
      }
    >
      {Array.isArray(selectedRows) && selectedRows.length > 1 && (
        <div className={classes.infoBox}>
          <InfoCircleOutlined className={classes.infoIcon} />
          <div className={classes.infoText}>
            {`Same information will be saved against all ${selectedRows?.length} selected records.`}
          </div>
        </div>
      )}

      {showReason && (
        <div className={classes.section}>
          <div className={classes.sectionLabel}>
            {t(`Please select the ${isDelivery ? 'NDR' : 'NPR'} Reason`)}
            <span style={{ color: 'red', marginLeft: 4 }}>*</span>
          </div>
          <Select
            showSearch
            allowClear
            placeholder={t(isDelivery ? 'Select NDR Reason' : 'Select NPR Reason')}
            value={selectedReasonId}
            onChange={(val) => setState({ selectedReasonId: val })}
            style={{ width: '100%' }}
            disabled={isLoading}
            optionFilterProp="children"
            aria-label={t(isDelivery ? 'Select NDR Reason' : 'Select NPR Reason')}
            aria-required={showReason}
            filterOption={(input, option) =>
              (option?.children as string)
                ?.toLowerCase()
                ?.includes(input.toLowerCase())}
          >
            {reasonList.map((reason) => (
              <Select.Option key={reason.id} value={reason.id}>
                {reason.name}
              </Select.Option>
            ))}
          </Select>
        </div>
      )}

      <div>
        <div className={classes.commentLabel}>{t('Comment')}</div>
        <Input.TextArea
          value={comment}
          onChange={(e) => setState({ comment: e.target.value })}
          rows={3}
          placeholder={t('Add notes or additional information...')}
          disabled={isLoading}
        />
      </div>
    </Modal>
  );
};

const mapStateToProps = ({ masterData }) => {
  return {
    allFailureReasons: masterData.all_failure_reasons,
  };
};

const NdrNprReasonModalStyled = withStyles(styles, { injectTheme: true })(NdrNprReasonModal);
export default connect(mapStateToProps, null)(NdrNprReasonModalStyled);
